import { colors } from '@/providers/theme/colors';
import {
  actionExecution,
  useAppDispatch,
  useAppSelector,
  usePrefetchImmediately,
} from '@/state';
import { ProjectsApi, ReleasesApi, UsersApi } from '@/state/api';
import { actions } from '@/state/constants/appActions';
import store from '@/state/store';
import { filterArrayByPermission } from '@/utils/permissionUtils';
import {
  Box,
  Center,
  HStack,
  Icon,
  IconButton,
  Image,
  Separator,
  Spacer,
} from '@chakra-ui/react';
import { AuthenticationFeatures } from '@features';
import {
  ColorModeButton,
  Drawer,
  type DynamicMenuType,
  PopoverLazy,
  toasterApi,
  TreeView,
} from '@ui';
import { useEffect, useState } from 'react';
import {
  AiOutlineMenuFold,
  AiOutlineReload,
  AiOutlineSetting,
  AiOutlineUserSwitch,
} from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router';
import ActionMenu from './ActionMenu';
import { navigationMenuBar } from './headerStructure';

const Header = () => {
  const [filteredMenu, setFilteredMenu] = useState(navigationMenuBar);
  const current = useAppSelector((state) => state.auth.current);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const filteredMenu = await filterArrayByPermission(navigationMenuBar);
        setFilteredMenu(filteredMenu as DynamicMenuType[]);
      } catch (error) {
        console.warn('Failed to filter menu by permissions:', error);
        toasterApi({
          type: 'error',
          status: 0,
          code: 'PermissionError',
          message: 'Permission Error',
          detail: 'You do not have permission to access this resource.',
        });

        if (current.userid) {
          actionExecution({
            action: actions.logoutCurrent,
            dispatch,
            args: {
              userid: current.userid,
            },
          });
        } else {
          actionExecution({
            action: actions.resetApp,
            dispatch,
          });
        }
      }
    };
    fetchTabs();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const reportRoute = location.pathname.startsWith('/t/');

  usePrefetchImmediately<any>([
    {
      api: ProjectsApi.Api,
      endpoint: 'getAllProjects',
    },
    {
      api: ReleasesApi.Api,
      endpoint: 'getAllRelease',
    },
    {
      api: UsersApi.Api,
      endpoint: 'getUserMe',
    },
  ]);

  return (
    <>
      <Box
        as="header"
        direction="column"
        width="100%"
        bg={colors.brand.primary.value}
        p={2}
      >
        <HStack justifyContent="space-between" width="100%">
          <HStack>
            <Image
              src="/logo-karas.png"
              alt="Karas Logo"
              width={{ base: '100px', md: '100px' }}
            />
          </HStack>
          <Spacer />
          <HStack>
            <Image
              src="/logo-infin8.png"
              alt="Infin8 Logo"
              width={{ base: '60px', md: '60px' }}
            />
          </HStack>
          <Spacer />
          <HStack>
            <Drawer
              trigger={
                <Icon aria-label="menu" color="fg" size={'lg'}>
                  <AiOutlineMenuFold />
                </Icon>
              }
              placement="end"
              title={
                <HStack>
                  <Box
                    textStyle={'heading.main'}
                    fontSize={{ base: 'sm', md: 'md' }}
                    color="accent1.fg"
                  >
                    Karas
                  </Box>
                </HStack>
              }
              header={
                <>
                  <Separator size="xs" />
                  <HStack gap={{ base: 1 }} alignSelf={'flex-end'}>
                    <IconButton
                      aria-label="refresh"
                      color="fg"
                      variant={'plain'}
                      onClick={() =>
                        actionExecution({
                          action: actions.refresh,
                          dispatch: store.dispatch,
                        })
                      }
                    >
                      <AiOutlineReload />
                    </IconButton>
                    <ColorModeButton />
                    <IconButton
                      aria-label="Settings"
                      color="fg"
                      variant={'plain'}
                      onClick={() => navigate('/profile')}
                    >
                      <AiOutlineSetting />
                    </IconButton>
                    <PopoverLazy
                      button={
                        <IconButton
                          aria-label="switch-user"
                          color="fg"
                          variant={'plain'}
                        >
                          <AiOutlineUserSwitch />
                        </IconButton>
                      }
                      popOverTitle="Switch User"
                    >
                      <AuthenticationFeatures.SelectUser />
                    </PopoverLazy>
                    <AuthenticationFeatures.Logout />
                  </HStack>
                </>
              }
            >
              <TreeView
                items={filteredMenu}
                title="Menu"
                labelProps={{ variant: 'plain', color: 'fg' }}
                iconProps={{ color: 'fg' }}
                onSelect={(e) => {
                  if (!e?.value?.startsWith('treeHeader')) {
                    navigate(e.items.action);
                  }
                }}
              />
            </Drawer>
          </HStack>
        </HStack>
      </Box>

      {!reportRoute && (
        <Center backgroundColor="#D9D9D9">
          <ActionMenu />
        </Center>
      )}
    </>
  );
};

export default Header;
